import { Injectable } from '@angular/core';
import { FirebaseService } from './firebase.service';
import { mergeMap, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { DesktopLicenseWithProduct, LicensesService } from './licenses.service';
import { Tenant } from './tenants.service';
import { SalesRep } from './sales-reps.service';
import { AuthService } from './auth.service';
import { ApiProduct } from './api-products.service';

export type DesktopInvoice = {
  id: string;
  number: string;
  tenant_id: string;
  initialized: boolean;
  completed: boolean;
  completed_at?: string;
  renewal: boolean;
  base_amount: number;
  tax_amount: number;
  total_amount: number;
  quickbooks_invoice_id?: string;
  quickbooks_payment_id?: string;
  status?: InvoiceStatus;
  expiration: string;
  created_at: string;
  use_manual_values: boolean;
  notes?: string | null;
};

export type InvoiceStatus = 'refunded' | 'voided' | null;

export type DesktopInvoiceDetails = {
  desktop_invoice: DesktopInvoice;
  license_details: DesktopLicenseInvoiceDetails[];
  not_renewing_licenses: DesktopLicenseWithProduct[];
  due_by: string;
  sales_reps: SalesRep[];
};

export type DesktopLicenseInvoiceDetails = {
  license_with_product: DesktopLicenseWithProduct;
  new_expiration_date: string;
  new_product_variant_id?: string;
  base_amount: number;
  tax_amount: number;
  total_amount: number;
};

export type ApiInvoice = {
  id: string;
  number: string;
  tenant_id: string;
  completed: boolean;
  completed_at?: string;
  base_amount: number;
  tax_amount: number;
  total_amount: number;
  commission_amount: number;
  transaction_yyyymm: string;
  job_count: number;
  quickbooks_invoice_id?: string;
  quickbooks_payment_id?: string;
  status?: InvoiceStatus;
  expiration: string;
  created_at: string;
  auto_pay_enabled: boolean;
  auto_pay_scheduled_at?: string;
  auto_pay_queued_at?: string;
  auto_pay_status?: ApiInvoiceAutoPayStatus | null;
  auto_pay_failure_reason?: string;
  auto_pay_processed_at?: string;
  auto_pay_started_at?: string;
};

export type ApiInvoiceAutoPayStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed';

export type ApiJobLog = {
  id: number;
  tenant_id: string;
  job_guid: string;
  filename: string;
  record_count: number;
  transaction_amount: number;
  transaction_date?: string;
  charged: boolean;
  created_at: string;
  updated_at: string;
};

export type SearchApiJobLogsResponse = {
  job_logs: ApiJobLog[];
  total: number;
  page: number;
  page_size: number;
};

export type SearchApiJobLogsParams = {
  page?: number;
  page_size?: number;
  month?: string;
  charged?: boolean;
};

export type AvailableMonthsResponse = {
  months: string[];
};

export type DesktopInvoicesResponse = {
  desktop_invoices: {
    pending: DesktopInvoiceDetails[];
    complete: DesktopInvoiceDetails[];
    expired: DesktopInvoiceDetails[];
  };
};

export type DesktopInvoiceResponse = {
  invoice_details: DesktopInvoiceDetails;
  tenant: Tenant;
};

export type DesktopInvoicesRecentResponse = {
  desktop_invoice: DesktopInvoice;
  tenant: Tenant;
};

export type ApiInvoiceDetails = {
  api_invoice: ApiInvoice;
  api_product: ApiProduct;
  sales_reps: SalesRep[];
};

export type ApiInvoiceResponse = {
  api_invoice_details: ApiInvoiceDetails;
  tenant: Tenant;
};

export type ApiInvoicesResponse = {
  api_invoices: {
    pending: ApiInvoice[];
    complete: ApiInvoice[];
    expired: ApiInvoice[];
  };
};

export type IgnoredApiInvoiceDetails = {
  tenant: Tenant;
  api_transaction_record: ApiTransactionRecord;
};

export type ApiInvoiceUploadResponse = {
  invoices: ApiInvoice[];
  no_cost_invoices: ApiInvoice[];
  ignored_invoices: IgnoredApiInvoiceDetails[];
  validate_errors: ValidateApiInvoiceRecordResponse[];
};

export type ApiInvoicesRecentResponse = {
  api_invoice_details: ApiInvoiceDetails;
  tenant: Tenant;
};

export type ApiTransactionRecord = {
  trans_yyyymm: string;
  account_number: string;
  company: string;
  api_type: string;
  sales_rep: string;
  job_count: number;
  invoice_amount: number;
  commission_amount: number;
  fromdate: string;
  todate: string;
};

export type ValidateApiInvoiceRecordResponse = {
  api_transaction_record: ApiTransactionRecord;
  tenant: Tenant;
  sales_rep: SalesRep;
  error: string;
};

export type GetRecentInvoicesRequest = {
  account_number?: string;
  company_name?: string;
  sales_rep_id?: number;
  transaction_yyyymm?: string;
  sort_by?: 'account_number' | 'company_name' | 'status';
  sort_direction?: 'ASC' | 'DESC';
  page_size: number;
  page_number: number;
  include_pending: boolean;
  include_expired: boolean;
  include_completed: boolean;
};

export type GetScheduledInvoicesRequest = {
  page_size: number;
  page_number: number;
  include_pending?: boolean;
  include_failed?: boolean;
  include_completed?: boolean;
  account_number?: string;
  company_name?: string;
};

export type CreateInvoiceRequest = {
  licenses: {
    desktop_license_id: string;
    new_product_variant_id?: string;
    manual_price?: number;
    manual_expiration?: string;
  }[];
  sales_rep_ids?: number[];
  initialized: boolean;
  notes?: string;
};

export type UpdateProductVariantForRenewalRequest = {
  invoice_id: string;
  variant_updates: ProductVariantUpdate[];
};

export type ProductVariantUpdate = {
  desktop_license_id: string;
  new_product_variant_id: string;
};

export type UpdateLicenseDurationRequest = {
  invoice_id: string;
  desktop_license_id: string;
  duration_months: number;
};

export type DeleteInvoiceResponse = {
  success: boolean;
};

export type GroupedLicenseInvoiceDetails = {
  license: DesktopLicenseInvoiceDetails;
  count: number;
};

export type GroupedLicenses = {
  license: DesktopLicenseWithProduct;
  count: number;
};

export type SendReceiptEmailResponse = {
  emailSent: boolean;
};

type SendPaymentReceiptEmailRequest = {
  emails: string[];
  invoice_id: string;
};

@Injectable({
  providedIn: 'root',
})
export class InvoicesService {
  getInvoiceDetailsUrl = environment.serverUrl + '/desktop-invoices/get';
  invoicesByTenantUrl = environment.serverUrl + '/desktop-invoices/for-tenant';
  getApiInvoiceDetailsUrl = environment.serverUrl + '/api-invoices/get';
  apiInvoicesByTenantUrl = environment.serverUrl + '/api-invoices/for-tenant';
  recentDesktopInvoicesUrl =
    environment.serverUrl + '/desktop-invoices/get-recent';
  recentApiInvoicesUrl = environment.serverUrl + '/api-invoices/get-recent';
  scheduledInvoicesUrl = environment.serverUrl + '/api-invoices/get-scheduled';
  currentTenantInvoicesUrl =
    environment.serverUrl + '/desktop-invoices/get-for-user';
  currentTenantApiInvoicesUrl =
    environment.serverUrl + '/api-invoices/get-for-user';
  getPendingInvoiceForTenantUrl =
    environment.serverUrl + '/desktop-invoices/get-pending-for-tenant';
  createInvoiceForLicensesUrl =
    environment.serverUrl + '/desktop-invoices/create-for-licenses';
  addLicenseToOrCreatePendingInvoiceUrl =
    environment.serverUrl + '/desktop-invoices/add-or-create-pending';
  validateApiInvoicesUrl =
    environment.serverUrl + '/api-invoices/validate-transactions';
  uploadApiInvoicesUrl =
    environment.serverUrl + '/api-invoices/upload-transactions';
  uploadJobLogsUrl = environment.serverUrl + '/api-invoices/upload-job-logs';
  queueApiInvoiceAutoPayUrl =
    environment.serverUrl + '/api-invoices/queue-auto-pay';
  pendingUnqueuedApiInvoicesCountUrl =
    environment.serverUrl + '/api-invoices/pending-unqueued-count';
  availableApiInvoiceTransactionMonthsUrl =
    environment.serverUrl + '/api-invoices/available-transaction-months';
  downloadApiInvoiceReceiptsUrl =
    environment.serverUrl + '/api-invoices/download-receipts';
  queueAllPendingApiInvoicesUrl =
    environment.serverUrl + '/api-invoices/queue-all-pending';
  disableApiInvoiceAutoPayUrl =
    environment.serverUrl + '/api-invoices/disable-auto-pay';
  removeLicenseFromInvoiceUrl =
    environment.serverUrl + '/desktop-invoices/remove-license';
  updateLicenseDurationUrl =
    environment.serverUrl + '/desktop-invoices/update-license-duration';
  updateLicenseProductVariantUrl =
    environment.serverUrl +
    '/desktop-invoices/update-product-variant-for-renewal';
  updateInvoiceInitializedUrl =
    environment.serverUrl + '/desktop-invoices/update-initialized';
  deleteInvoiceUrl = environment.serverUrl + '/desktop-invoices/delete';
  deleteApiInvoiceUrl = environment.serverUrl + '/api-invoices/delete';
  sendReceiptEmailUrl =
    environment.serverUrl + '/desktop-invoices/send-receipt-email';
  sendApiReceiptEmailUrl =
    environment.serverUrl + '/api-invoices/send-receipt-email';
  updateDesktopInvoiceStatusUrl =
    environment.serverUrl + '/desktop-invoices/update-status';
  updateApiInvoiceStatusUrl =
    environment.serverUrl + '/api-invoices/update-status';

  pendingInvoice?: DesktopInvoiceDetails;
  fetchingPendingInvoice = false;

  constructor(
    private authService: AuthService,
    private firebaseService: FirebaseService,
    private licensesService: LicensesService,
    private http: HttpClient
  ) {
    this.fetchPendingInvoice();
  }

  fetchPendingInvoice() {
    console.log('fetching pending invoice');

    if (!this.authService.currentUser) {
      return;
    }

    this.fetchingPendingInvoice = true;
    return new Promise<DesktopInvoiceDetails>((resolve, reject) => {
      this.getPendingInvoiceForTenant(
        this.authService.currentUser!.tenant_id
      ).subscribe({
        next: (invoice) => {
          console.log('cart updated: ', invoice);
          this.fetchingPendingInvoice = false;
          this.pendingInvoice = invoice;
          resolve(invoice);
        },
        error: (error) => {
          console.error(error);
          this.fetchingPendingInvoice = false;
          reject(error);
        },
      });
    });
  }

  isInPendingInvoice(licenseId: string) {
    return this.pendingInvoice?.license_details.some(
      (l) => l.license_with_product.id === licenseId
    );
  }

  getInvoiceDetails(invoiceId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<DesktopInvoiceResponse>(
          this.getInvoiceDetailsUrl + '/' + invoiceId,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getApiInvoiceDetails(apiInvoiceId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<ApiInvoiceResponse>(
          this.getApiInvoiceDetailsUrl + '/' + apiInvoiceId,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getInvoicesForCurrentTenant() {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<DesktopInvoicesResponse>(
          this.currentTenantInvoicesUrl,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getApiInvoicesForCurrentTenant() {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<ApiInvoicesResponse>(
          this.currentTenantApiInvoicesUrl,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getInvoicesForTenant(tenantId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<DesktopInvoicesResponse>(
          this.invoicesByTenantUrl + '/' + tenantId,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getApiInvoicesForTenant(tenantId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<ApiInvoicesResponse>(
          this.apiInvoicesByTenantUrl + '/' + tenantId,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getPendingInvoiceForTenant(tenantId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<DesktopInvoiceDetails>(
          this.getPendingInvoiceForTenantUrl + '/' + tenantId,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getRecentDesktopInvoices(request: GetRecentInvoicesRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<DesktopInvoicesRecentResponse[]>(
          this.recentDesktopInvoicesUrl,
          request,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getRecentApiInvoices(request: GetRecentInvoicesRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<ApiInvoicesRecentResponse[]>(
          this.recentApiInvoicesUrl,
          request,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getScheduledInvoices(request: GetScheduledInvoicesRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<ApiInvoicesRecentResponse[]>(
          this.scheduledInvoicesUrl,
          request,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  queueAutoPayForApiInvoices(invoiceIds: string[]) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post(
          this.queueApiInvoiceAutoPayUrl,
          { invoice_ids: invoiceIds },
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getAvailableApiInvoiceTransactionMonths() {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<{ transaction_yyyymm: string[] }>(
          this.availableApiInvoiceTransactionMonthsUrl,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  downloadApiInvoiceReceiptsByMonth(month: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get(this.downloadApiInvoiceReceiptsUrl + '/' + month, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
          responseType: 'blob',
        });
      })
    );
  }

  getPendingUnqueuedApiInvoicesCount() {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<{ count: number }>(
          this.pendingUnqueuedApiInvoicesCountUrl,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  queueAllPendingApiInvoices() {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<{ api_invoices: ApiInvoice[] }>(
          this.queueAllPendingApiInvoicesUrl,
          {},
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  disableApiInvoiceAutoPay(invoiceId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<ApiInvoiceResponse>(
          this.disableApiInvoiceAutoPayUrl,
          { invoice_id: invoiceId },
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  createInvoice(request: CreateInvoiceRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<DesktopInvoiceResponse>(
          this.createInvoiceForLicensesUrl,
          request,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  addLicenseToOrCreatePendingInvoice(
    licenseId: string,
    newProductVariantId?: string
  ) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http
          .post<DesktopInvoiceResponse>(
            this.addLicenseToOrCreatePendingInvoiceUrl,
            {
              license_id: licenseId,
              new_product_variant_id: newProductVariantId,
            },
            {
              headers: {
                Authorization: 'Bearer ' + token,
              },
            }
          )
          .pipe(
            tap((res) => {
              this.pendingInvoice = res.invoice_details;
            })
          );
      })
    );
  }

  validateApiInvoices(file: File) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        const formData = new FormData();
        formData.append('files', file);
        return this.http.post<ValidateApiInvoiceRecordResponse[]>(
          this.validateApiInvoicesUrl,
          formData,
          { headers: { Authorization: 'Bearer ' + token } }
        );
      })
    );
  }

  uploadApiInvoices(file: File) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        const formData = new FormData();
        formData.append('files', file);
        return this.http.post<ApiInvoiceUploadResponse>(
          this.uploadApiInvoicesUrl,
          formData,
          { headers: { Authorization: 'Bearer ' + token } }
        );
      })
    );
  }

  uploadJobLogs(files: File[]) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        const formData = new FormData();
        for (const file of files) {
          formData.append('files', file);
        }
        return this.http.post<{
          files: {
            filename: string;
            success: boolean;
            error?: string;
            job_count: number;
          }[];
        }>(this.uploadJobLogsUrl, formData, {
          headers: { Authorization: 'Bearer ' + token },
        });
      })
    );
  }

  removeLicenseFromInvoice(invoiceId: string, licenseId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http
          .post<DesktopInvoiceResponse>(
            this.removeLicenseFromInvoiceUrl,
            {
              license_id: licenseId,
              invoice_id: invoiceId,
            },
            {
              headers: {
                Authorization: 'Bearer ' + token,
              },
            }
          )
          .pipe(
            tap((res) => {
              // if the invoice is the pending invoice, update the pending invoice
              if (this.pendingInvoice?.desktop_invoice.id === invoiceId) {
                if (res == null) {
                  // invoice was deleted
                  this.pendingInvoice = undefined;
                  return;
                }
                this.pendingInvoice = res.invoice_details;
              }
            })
          );
      })
    );
  }

  updateLicenseDuration(req: UpdateLicenseDurationRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http
          .post<DesktopInvoiceResponse>(this.updateLicenseDurationUrl, req, {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          })
          .pipe(
            tap((res) => {
              if (this.pendingInvoice?.desktop_invoice.id === req.invoice_id) {
                this.pendingInvoice = res.invoice_details;
              }
            })
          );
      })
    );
  }

  updateLicenseProductVariant(req: UpdateProductVariantForRenewalRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http
          .post<DesktopInvoiceResponse>(
            this.updateLicenseProductVariantUrl,
            req,
            {
              headers: {
                Authorization: 'Bearer ' + token,
              },
            }
          )
          .pipe(
            tap((res) => {
              this.pendingInvoice = res.invoice_details;
            })
          );
      })
    );
  }

  updateInvoiceInitialized(invoiceId: string, initialized: boolean) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<DesktopInvoiceResponse>(
          this.updateInvoiceInitializedUrl,
          { invoice_id: invoiceId, initialized: initialized },
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  deleteInvoice(invoiceId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.delete<DeleteInvoiceResponse>(
          this.deleteInvoiceUrl + '/' + invoiceId,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  deleteApiInvoice(invoiceId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.delete<DeleteInvoiceResponse>(
          this.deleteApiInvoiceUrl + '/' + invoiceId,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  groupLicensesByProduct(
    licenses: DesktopLicenseWithProduct[]
  ): GroupedLicenses[] {
    const grouped: GroupedLicenses[] = [];

    for (const license of licenses) {
      const existing = grouped.find(
        (l) =>
          l.license.product_variant.id === license.product_variant.id &&
          l.license.site_license === license.site_license &&
          l.license.renewal === license.renewal
      );

      if (existing) {
        existing.count++;
      } else {
        grouped.push({ license, count: 1 });
      }
    }

    return grouped;
  }

  groupLicenseDetailsByRelation(
    licenseDetails: DesktopLicenseInvoiceDetails[]
  ) {
    const licenses = licenseDetails.map((l) => l.license_with_product);
    const sortedLicenses =
      this.licensesService.sortLicensesByRelationship(licenses);

    const groupedLicenseDetails: GroupedLicenseInvoiceDetails[] = [];
    for (const license of sortedLicenses) {
      const details = licenseDetails.find(
        (l) => l.license_with_product.id === license.id
      );
      if (
        details &&
        groupedLicenseDetails.find((l) =>
          this.licensesService.licenseTypesMatch(
            l.license.license_with_product,
            license
          )
        )
      ) {
        continue;
      }
      const count = sortedLicenses.filter((l) =>
        this.licensesService.licenseTypesMatch(l, license)
      ).length;

      if (details) {
        groupedLicenseDetails.push({ license: details, count: count });
      }
    }

    return groupedLicenseDetails;
  }

  getLicensePrice(license: DesktopLicenseWithProduct) {
    if (license.renewal && license.site_license) {
      return license.product_variant.site_renewal_price;
    } else if (license.renewal) {
      return license.product_variant.renewal_price;
    } else if (license.site_license) {
      return license.product_variant.site_price;
    } else {
      return license.product_variant.price;
    }
  }

  sendReceiptEmail(invoiceId: string, emails: string[]) {
    console.log('Sending receipt email to: ', emails);
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        const request: SendPaymentReceiptEmailRequest = {
          emails: emails,
          invoice_id: invoiceId,
        };

        return this.http.post<SendReceiptEmailResponse>(
          this.sendReceiptEmailUrl,
          request,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  sendApiReceiptEmail(invoiceId: string, emails: string[]) {
    console.log('Sending API receipt email to: ', emails);
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        const request: SendPaymentReceiptEmailRequest = {
          emails: emails,
          invoice_id: invoiceId,
        };

        return this.http.post<SendReceiptEmailResponse>(
          this.sendApiReceiptEmailUrl,
          request,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  getDisplayTransactionMonth(yyyymm: string): Date {
    return new Date(Number(yyyymm.slice(0, 4)), Number(yyyymm.slice(4, 6)) - 1);
  }

  getApiJobLogsForTenant(tenantId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        let url = `${environment.serverUrl}/api-invoices/job-logs/${tenantId}`;
        return this.http.get<ApiJobLog[]>(url, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });
      })
    );
  }

  getAvailableMonthsForTenant(tenantId: string) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<AvailableMonthsResponse>(
          `${environment.serverUrl}/api-invoices/job-logs/${tenantId}/available-months`,
          {
            headers: {
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  updateDesktopInvoiceStatus(desktopInvoiceId: string, status: InvoiceStatus) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<DesktopInvoice>(
          this.updateDesktopInvoiceStatusUrl,
          {
            invoice_id: desktopInvoiceId,
            status: status,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }

  updateApiInvoiceStatus(apiInvoiceId: string, status: InvoiceStatus) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<ApiInvoice>(
          this.updateApiInvoiceStatusUrl,
          {
            invoice_id: apiInvoiceId,
            status: status,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + token,
            },
          }
        );
      })
    );
  }
}
